// hooks/useTimeTracker.ts
import { useState, useCallback } from 'react';
import { TaskForSubmission, TrackingProgress } from '../types';
import { trackTime } from '../../../../gitlabApi/gqlApi/trackTime';

interface UseTimeTrackerReturn {
  trackTime: (
    tasks: TaskForSubmission[],
    onProgress?: (progress: TrackingProgress) => void,
  ) => Promise<string[]>;
  isTracking: boolean;
  error: string | null;
}

export const useTimeTracker = (): UseTimeTrackerReturn => {
  const [isTracking, setIsTracking] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Функция для округления времени до ближайшего большего значения кратного 15
  const roundToFifteenMinutes = (timeInSeconds: number): number => {
    const minutes = timeInSeconds / 60;
    return Math.ceil(minutes / 15) * 15;
  };

  const trackTimeBatch = useCallback(
    async (
      tasks: TaskForSubmission[],
      onProgress?: (progress: TrackingProgress) => void,
    ): Promise<string[]> => {
      setIsTracking(true);
      setError(null);

      const successfullyTrackedTaskIds: string[] = [];
      const errors: string[] = [];

      try {
        // Отправляем начальный статус
        onProgress?.({
          status: 'starting',
          totalTasks: tasks.length,
          completedTasks: 0,
        });

        // Обрабатываем каждую задачу отдельно
        for (let i = 0; i < tasks.length; i++) {
          const task = tasks[i];

          try {
            // Пропускаем задачи без потраченного времени
            if (task.timeSpent === 0) {
              continue;
            }

            // Уведомляем о начале трекинга текущей задачи
            onProgress?.({
              status: 'tracking',
              currentTask: task.name,
              totalTasks: tasks.length,
              completedTasks: i,
            });

            // Округляем время до 15 минут
            const roundedMinutes = roundToFifteenMinutes(task.timeSpent);
            const timeSpentFormatted = `${roundedMinutes}m`;

            console.log(
              `Отправка задачи ${task.name}: ${
                task.timeSpent / 60
              } мин -> ${roundedMinutes} мин (${timeSpentFormatted})`,
            );

            // Вызываем внешнюю функцию
            const success = await trackTime({
              issuableId: task.slug,
              timeSpent: timeSpentFormatted,
              summary: task.comment || '',
            });

            if (success) {
              successfullyTrackedTaskIds.push(task.id);
              console.log(`✅ Успешно отправлена задача: ${task.name}`);
            } else {
              errors.push(`Не удалось отправить задачу: ${task.name}`);
              console.log(`❌ Ошибка отправки задачи: ${task.name}`);
            }
          } catch (taskError) {
            const taskErrorMessage =
              taskError instanceof Error ? taskError.message : 'Неизвестная ошибка';
            errors.push(`Ошибка при отправке задачи "${task.name}": ${taskErrorMessage}`);
            console.error(`❌ Ошибка при отправке задачи ${task.name}:`, taskError);
          }
        }

        // Отправляем финальный статус
        onProgress?.({
          status: 'finished',
          totalTasks: tasks.length,
          completedTasks: tasks.length,
        });

        // Если есть ошибки, показываем их пользователю
        if (errors.length > 0) {
          setError(errors.join('\n'));
        }

        console.log(
          `Итого отправлено успешно: ${successfullyTrackedTaskIds.length} из ${tasks.length} задач`,
        );

        return successfullyTrackedTaskIds;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : 'Общая ошибка отправки данных';
        setError(errorMessage);
        console.error('Общая ошибка трекинга времени:', err);
        return successfullyTrackedTaskIds;
      } finally {
        setIsTracking(false);
        // Сбрасываем прогресс после завершения
        setTimeout(() => {
          onProgress?.({ status: 'idle' });
        }, 2000);
      }
    },
    [],
  );

  return { trackTime: trackTimeBatch, isTracking, error };
};
